import React, {
  Suspense,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Paper,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import AssignmentIndRoundedIcon from "@mui/icons-material/AssignmentIndRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PersonPinCircleRoundedIcon from "@mui/icons-material/PersonPinCircleRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "contexts/AuthContext";
import { OVERLAY_NAMES } from "constants/Overlays";
import Overlay from "components/Overlay/Overlay";
import api from "services/api";
import { normalizePhoneNumber } from "helpers/ServiceHelpers";
import { getMileageRateForDate } from "helpers/mileageRateHelper";
import {
  formatTimeToHHMM12Hr,
  formatDurationToHHMM,
} from "helpers/assignmentPlaceholders";
import { getOutreachLocation } from "helpers/locationHelper";
import { EST_TIMEZONE, getTimezoneAbbreviation } from "helpers/timezoneHelpers";
import {
  EMPTY_LINGUIST_CLIENT_STATS,
  buildLinguistTooltipContent,
  formatLastAssignmentAgo,
} from "helpers/linguistTooltipHelper";

// Lazy so MapLibre only loads when the feature flag renders the map.
const LinguistDistanceMap = React.lazy(() =>
  import("components/LinguistDistanceMap/LinguistDistanceMap"),
);

const normalizeCoordinate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const hasValidCoordinates = (value) =>
  normalizeCoordinate(value?.lat) != null &&
  normalizeCoordinate(value?.lon) != null;

const OUTREACH_STATUSES = [
  "Not Reached",
  // Reached out, awaiting the linguist's reply (2026-10-01, per Amine) —
  // distinct from "No Response" (which stays unresolved after follow-ups).
  "Waiting for Answer",
  "No Response",
  "Will Confirm Back",
  "Not Available",
  "Accepted",
];

// Status → theme color mapping for the outreach table:
//   Not Reached         → neutral grey (no activity yet)
//   Waiting for Answer  → info (asked, awaiting reply)
//   No Response         → warning (waiting, unresolved)
//   Will Confirm Back   → info (pending confirmation)
//   Not Available       → error (declined)
//   Accepted            → primary (positive outcome)
const getStatusColor = (theme, status) => {
  switch (status) {
    case "Waiting for Answer":
      return theme.palette.info.main;
    case "No Response":
      return theme.palette.warning.main;
    case "Will Confirm Back":
      return theme.palette.info.main;
    case "Not Available":
      return theme.palette.error.main;
    case "Accepted":
      return theme.palette.primary.main;
    default:
      return theme.palette.grey[500];
  }
};

const InterpretationOutreach = () => {
  const theme = useTheme();
  // Wide screens (≥1200px) render the table; narrower screens render cards.
  const isWide = useMediaQuery("(min-width:1200px)");

  const {
    overlayData,
    setOverlayData,
    setOverlayName,
    showSnackbar,
    triggerPageRefresh,
  } = useStateContext();
  const { socket } = useWebSocket();
  const { currentUser } = useAuth();

  const assignment = useMemo(
    () => overlayData?.Assignment || {},
    [overlayData?.Assignment],
  );
  const serviceId = Number(overlayData?.ServiceID || assignment?.id || 0);
  const allLinguists = useMemo(
    () => (Array.isArray(overlayData?.Linguists) ? overlayData.Linguists : []),
    [overlayData?.Linguists],
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingStatusId, setSavingStatusId] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  const loadedRef = useRef(false);

  // Distance / mileage / assignment stats (same logic as Pre-Assign).
  const [mileageRates, setMileageRates] = useState([]);
  const [linguistDistancesById, setLinguistDistancesById] = useState({});
  const [isCalculatingDistances, setIsCalculatingDistances] = useState(false);
  const [distanceCalculationFailed, setDistanceCalculationFailed] = useState(false);
  const [distanceSource, setDistanceSource] = useState("");
  const [linguistClientStatsById, setLinguistClientStatsById] = useState({});
  const [assignmentCoords, setAssignmentCoords] = useState(null);
  const [selectedLinguistId, setSelectedLinguistId] = useState(null);
  // Runtime capability from the distance response: production stays hidden
  // until the server enables it, with no client rebuild needed.
  const [detailTab, setDetailTab] = useState(0);
  const lastDistanceFetchKeyRef = useRef("");
  const inFlightDistanceFetchKeyRef = useRef("");

  const getMileageRateForYear = (dateValue) => {
    if (!dateValue) return 0.7;
    return getMileageRateForDate(mileageRates, dateValue, 0.7);
  };

  const assignedLinguist = assignment?.Linguist || null;

  const targetLanguageName = useMemo(
    () =>
      String(
        assignment?.ServiceTargetLanguages?.[0]?.Language?.Name || "",
      ).trim(),
    [assignment],
  );

  // Candidate linguists: already in outreach rows OR match the assignment's
  // language and are "Current" (excluding the N/A placeholder linguist).
  const candidateLinguists = useMemo(() => {
    const rowLinguistIds = new Set(
      rows.map((row) => Number(row?.LinguistID || 0)),
    );
    const matched = allLinguists.filter((ling) => {
      if (Number(ling?.id || 0) === 1) return false; // N/A linguist
      if (String(ling?.Name || "").trim() === "N/A") return false;
      if (rowLinguistIds.has(Number(ling?.id || 0))) return true;

      const isCurrent = String(ling?.Status || "").trim() === "Current";
      const languageMatches = targetLanguageName
        ? (ling?.LinguistLanguages || []).some(
            (ll) =>
              String(ll?.Language?.Name || "").trim() === targetLanguageName,
          )
        : false;
      return isCurrent && languageMatches;
    });
    return matched.sort((a, b) =>
      String(a?.Name || "").localeCompare(String(b?.Name || "")),
    );
  }, [allLinguists, rows, targetLanguageName]);

  // Keep the latest snackbar reachable from stable callbacks without forcing
  // effect re-runs (a changing callback identity was reloading the overlay).
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  const loadRows = useCallback(async () => {
    if (!serviceId) return;
    setLoading(true);
    try {
      const response = await api.get(`/outreach/service/${serviceId}`);
      console.log("[InterpretationOutreach] loadRows response:", response?.data);
      setRows(response.data || []);
    } catch (error) {
      console.error("[InterpretationOutreach] Error loading outreach rows:", error);
      showSnackbarRef.current("Failed to load outreach", theme, "error");
    } finally {
      setLoading(false);
    }
  }, [serviceId, theme]);

  // Broadcast outreach changes so other users with the SAME assignment's
  // outreach overlay open refetch live (no lock system).
  const emitOutreachSaved = useCallback(() => {
    if (!socket || !currentUser || !serviceId) return;
    socket.emit("outreach:saved", {
      serviceId,
      user: {
        id: currentUser.id,
        name: currentUser.Name,
        imageThumbnail: currentUser.ImageThumbnail,
      },
    });
  }, [socket, currentUser, serviceId]);

  // Silent refetch (no loading spinner churn) used when another user updates
  // this assignment's outreach table.
  const refreshRowsSilently = useCallback(async () => {
    if (!serviceId) return;
    try {
      const response = await api.get(`/outreach/service/${serviceId}`);
      console.log("[InterpretationOutreach] refreshRowsSilently response:", response?.data);
      setRows(response.data || []);
    } catch (error) {
      console.error("[InterpretationOutreach] Error refreshing outreach rows:", error);
    }
  }, [serviceId]);

  // Live sync: another user changed a status / assigned a linguist on this
  // assignment's outreach table.
  useEffect(() => {
    if (!socket) return;

    const handleOutreachUpdated = (payload = {}) => {
      if (Number(payload?.serviceId) !== serviceId) return;
      const updatedBy = payload.updatedBy || {};
      const isSelfEdit =
        updatedBy.userId &&
        Number(updatedBy.userId) === Number(currentUser?.id);

      refreshRowsSilently();

      if (!isSelfEdit) {
        showSnackbarRef.current(
          `${updatedBy.userName || "Another member"} updated the outreach list.`,
          theme,
          "info",
        );
      }
    };

    socket.on("outreach:updated", handleOutreachUpdated);
    return () => socket.off("outreach:updated", handleOutreachUpdated);
  }, [socket, serviceId, currentUser?.id, refreshRowsSilently, theme]);

  // Load once per service only — status updates must NOT reload the overlay.
  useEffect(() => {
    loadRows();
  }, [serviceId, loadRows]);

  // Auto-ensure rows for candidate linguists once, after rows are loaded.
  useEffect(() => {
    if (loading || loadedRef.current || !serviceId) return;
    if (candidateLinguists.length === 0) return;

    const candidateIds = candidateLinguists.map((ling) => Number(ling.id));
    const rowIds = new Set(rows.map((row) => Number(row?.LinguistID || 0)));
    const missing = candidateIds.filter((id) => !rowIds.has(id));
    if (missing.length === 0) {
      loadedRef.current = true;
      return;
    }

    loadedRef.current = true;
    api
      .post(`/outreach/service/${serviceId}/ensure`, {
        linguistIds: missing,
      })
      .then((response) => {
        console.log("[InterpretationOutreach] ensure outreach rows response:", response?.data);
        setRows(response.data || []);
        // New outreach rows were created for candidate linguists — let other
        // open overlays know so they refetch too.
        emitOutreachSaved();
      })
      .catch((error) => console.error("[InterpretationOutreach] Error ensuring outreach rows:", error));
  }, [loading, candidateLinguists, rows, serviceId, emitOutreachSaved]);

  // Mileage rates (same as Pre-Assign).
  useEffect(() => {
    api
      .get("/mileageRates")
      .then((res) => setMileageRates(res.data || []))
      .catch(() => {});
  }, []);

  // Row linguist ids drive the distance + stats fetches below.
  const rowLinguistIdsSignature = useMemo(() => {
    const ids = [
      ...new Set(
        rows
          .map((row) => Number(row?.LinguistID || 0))
          .filter((id) => id > 0),
      ),
    ].sort((a, b) => a - b);
    return ids.join(",");
  }, [rows]);

  const destinationAddress = String(assignment?.Address || "").trim();

  // Driving distances (debounced, same logic as Pre-Assign).
  useEffect(() => {
    let isCancelled = false;

    const linguistIds = rowLinguistIdsSignature
      ? rowLinguistIdsSignature.split(",").map((id) => Number(id))
      : [];

    if (!destinationAddress || !linguistIds.length) {
      setIsCalculatingDistances(false);
      setDistanceCalculationFailed(false);
      setLinguistDistancesById({});
      setDistanceSource("");
      setAssignmentCoords(null);
      setSelectedLinguistId(null);
      return undefined;
    }

    const requestKey = `${destinationAddress.toLowerCase()}|${rowLinguistIdsSignature}`;

    if (inFlightDistanceFetchKeyRef.current === requestKey) {
      return undefined;
    }

    if (lastDistanceFetchKeyRef.current === requestKey) {
      setIsCalculatingDistances(false);
      return undefined;
    }

    inFlightDistanceFetchKeyRef.current = requestKey;
    setIsCalculatingDistances(true);
    setDistanceCalculationFailed(false);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await api.post("/geocoding/driving-distances", {
          destinationAddress,
          linguistIds,
        });
        console.log("[InterpretationOutreach] driving distances response:", response?.data);

        if (!isCancelled) {
          const responseData = response?.data || {};
          setLinguistDistancesById(responseData);
          const rawAssignment = responseData?.__assignment;
          const assignmentLat = normalizeCoordinate(rawAssignment?.lat);
          const assignmentLon = normalizeCoordinate(rawAssignment?.lon);
          setAssignmentCoords(
            assignmentLat != null && assignmentLon != null
              ? { lat: assignmentLat, lon: assignmentLon }
              : null,
          );
          const sourceFromBody = String(
            responseData?.__distanceSource || "",
          ).toLowerCase();
          const sourceFromHeader = String(
            response?.headers?.["x-distance-source"] || "",
          ).toLowerCase();
          setDistanceSource(sourceFromBody || sourceFromHeader || "unknown");
          const responseLinguistKeys = Object.keys(responseData).filter(
            (key) => !key.startsWith("__"),
          );
          const responseWithCoordinates = responseLinguistKeys.filter(
            (key) =>
              responseData[key]?.lat != null &&
              responseData[key]?.lon != null,
          ).length;
          console.info(
            "[InterpretationOutreach][Map] distance response " +
              JSON.stringify({
                source: sourceFromBody || sourceFromHeader || "unknown",
                assignment: Boolean(rawAssignment),
                linguists: responseLinguistKeys.length,
                withCoordinates: responseWithCoordinates,
              }),
          );
          setDistanceCalculationFailed(false);
          lastDistanceFetchKeyRef.current = requestKey;
          inFlightDistanceFetchKeyRef.current = "";
          setIsCalculatingDistances(false);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("[InterpretationOutreach] driving distance request failed:", error);
          inFlightDistanceFetchKeyRef.current = "";
          setLinguistDistancesById({});
          setDistanceSource("");
          setAssignmentCoords(null);
          setDistanceCalculationFailed(true);
          setIsCalculatingDistances(false);
          showSnackbarRef.current(
            "There was an issue with the Distance Calculation Server. Please try again later, or manually check the distance using Google Maps.",
            theme,
            "error",
          );
        }
      }
    }, 800);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
      if (inFlightDistanceFetchKeyRef.current === requestKey) {
        inFlightDistanceFetchKeyRef.current = "";
      }
    };
  }, [destinationAddress, rowLinguistIdsSignature, theme]);

  // Linguist client stats (completed assignments, same as Pre-Assign).
  const clientId = Number(
    assignment?.ClientID || assignment?.Client?.id || 0,
  );

  useEffect(() => {
    let isCancelled = false;

    const linguistIds = rowLinguistIdsSignature
      ? rowLinguistIdsSignature.split(",").map((id) => Number(id))
      : [];

    if (!clientId || !linguistIds.length) {
      setLinguistClientStatsById({});
      return undefined;
    }

    const fetchStats = async () => {
      try {
        const response = await api.post("/linguists/client-service-stats", {
          clientId,
          linguistIds,
        });
        console.log("[InterpretationOutreach] client-service-stats response:", response?.data);
        if (!isCancelled) {
          setLinguistClientStatsById(response.data || {});
        }
      } catch {
        if (!isCancelled) {
          setLinguistClientStatsById({});
        }
      }
    };

    fetchStats();

    return () => {
      isCancelled = true;
    };
  }, [clientId, rowLinguistIdsSignature]);

  // Rows sorted by distance (nulls last, then name) — same ordering as
  // InterpretationPreAssign's filtered linguist list.
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const aMiles =
        linguistDistancesById[Number(a?.LinguistID || 0)]?.distanceMiles ??
        null;
      const bMiles =
        linguistDistancesById[Number(b?.LinguistID || 0)]?.distanceMiles ??
        null;
      if (aMiles == null && bMiles == null) {
        return String(a?.Linguist?.Name || "").localeCompare(
          String(b?.Linguist?.Name || ""),
        );
      }
      if (aMiles == null) return 1;
      if (bMiles == null) return -1;
      return aMiles - bMiles;
    });
  }, [rows, linguistDistancesById]);

  const handleStatusChange = async (row, nextStatus) => {
    if (nextStatus === row.Status) return;
    const previous = rows;
    setRows((prev) =>
      prev.map((r) =>
        Number(r.id) === Number(row.id)
          ? { ...r, Status: nextStatus, LastContactedAt: new Date().toISOString() }
          : r,
      ),
    );
    setSavingStatusId(row.id);
    try {
      const response = await api.put(`/outreach/${row.id}`, {
        Status: nextStatus,
      });
      setRows((prev) =>
        prev.map((r) =>
          Number(r.id) === Number(row.id) ? response.data : r,
        ),
      );
      emitOutreachSaved();
      showSnackbar(
        `Status updated to "${nextStatus}"`,
        theme,
        "success",
      );
    } catch (error) {
      setRows(previous);
      showSnackbar(
        error?.response?.data?.error || "Failed to update status",
        theme,
        "error",
      );
    } finally {
      setSavingStatusId(null);
    }
  };

  const openSendDetails = (row, method) => {
    const linguist = row?.Linguist || {};
    setOverlayData({
      Assignment: assignment,
      RecipientLinguist: {
        id: linguist.id,
        Name: linguist.Name,
      },
      PrefillMethod: method,
    });
    setOverlayName(OVERLAY_NAMES.INTERPRETATION_SEND_DETAILS);
  };

  // Direct availability text (2026-10-01, per Amine): the SMS button opens
  // the phone's SMS app with the availability-check message prefilled —
  // number from the primary phone-table entry, times in EST/EDT, the
  // location REDUCED to City/State/ZIP (the street is never shared while
  // checking availability — some linguists are home-based), and the message
  // signed with the clicking member's first name.
  const buildAvailabilitySmsBody = (linguist) => {
    const firstName = String(linguist?.ShortName || linguist?.Name || "")
      .trim()
      .split(/\s+/)[0];

    const details = String(
      assignment?.Name || assignment?.Details || "",
    ).trim();

    const dateText = assignment?.Date
      ? dayjs(assignment.Date).format("dddd, MMMM DD, YYYY")
      : "";

    const startText = assignment?.TimeStart
      ? formatTimeToHHMM12Hr(assignment.TimeStart)
      : "";
    const endText = assignment?.TimeEnd
      ? formatTimeToHHMM12Hr(assignment.TimeEnd)
      : "";
    const durationText = assignment?.Duration
      ? formatDurationToHHMM(assignment.Duration)
      : "";
    const tzText = getTimezoneAbbreviation(
      EST_TIMEZONE,
      assignment?.Date ? dayjs(assignment.Date).toDate() : new Date(),
    );
    const timeText =
      startText && endText
        ? `${startText} - ${endText} ${tzText}${
            durationText ? ` (${durationText})` : ""
          }`
        : "";

    // City, State ZIP only — the street part is intentionally dropped
    // (getOutreachLocation is privacy-first: it never returns the street).
    const locationText = getOutreachLocation(assignment?.Address || "");

    const signer = String(currentUser?.Name || "")
      .trim()
      .split(/\s+/)[0];

    const blocks = [
      firstName ? `Hello ${firstName},` : "Hello,",
      "I hope you're doing well today!",
      "Would you be available for this assignment?",
      details ? `📌 Assignment: ${details}` : "",
      dateText ? `📅 Date: ${dateText}` : "",
      timeText ? `⏰ Time: ${timeText}` : "",
      locationText ? `📍 Location: ${locationText}` : "",
      signer ? `-${signer}` : "",
    ];

    return blocks.filter(Boolean).join("\n\n");
  };

  const handleTextAvailability = (row, callPhone) => {
    if (!callPhone) return;
    const body = buildAvailabilitySmsBody(row?.Linguist);
    const smsLink = `sms:${callPhone};?&body=${encodeURIComponent(body)}`;
    window.open(smsLink, "_blank", "noopener");
  };

  const handleAssign = async (row) => {
    setAssigningId(row.id);
    try {
      const response = await api.post(
        `/outreach/service/${serviceId}/assign/${row.id}`,
      );
      setRows((prev) =>
        prev.map((r) =>
          Number(r.id) === Number(row.id) ? response.data : r,
        ),
      );
      showSnackbar(
        `${row?.Linguist?.Name || "Linguist"} assigned to the assignment`,
        theme,
        "success",
      );
      triggerPageRefresh("Outreach updated", theme);
      emitOutreachSaved();
    } catch (error) {
      console.error("Error assigning linguist:", error);
      showSnackbar(
        error?.response?.data?.error || "Failed to assign linguist",
        theme,
        "error",
      );
    } finally {
      setAssigningId(null);
    }
  };

  const renderHistory = (row) => {
    const history = Array.isArray(row?.History) ? row.History : [];
    if (history.length === 0) {
      return <Typography variant="body2">No history yet.</Typography>;
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {history
          .slice()
          .reverse()
          .map((event, index) => (
            <Box key={index}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {dayjs(event?.at).format("MMM DD, h:mm A")}
                {event?.byName || event?.by ? ` • ${event?.byName || `User #${event.by}`}` : ""}
              </Typography>
              <Typography variant="body2">
                {event?.action === "Status changed"
                  ? `Status: ${event?.from} → ${event?.to}`
                  : event?.action}
              </Typography>
            </Box>
          ))}
      </Box>
    );
  };

  const phoneOf = (linguist) => {
    const phones = Array.isArray(linguist?.PhoneNumbers)
      ? linguist.PhoneNumbers.filter((p) => String(p?.Number || "").trim())
      : [];
    if (phones.length > 0) {
      const primary =
        phones.find((p) => p.IsPrimary) || phones[0];
      const withPrefix =
        String(primary?.CountryCode || "") &&
        String(primary?.CountryCode) !== "1"
          ? `+${primary.CountryCode}${primary.Number}`
          : primary.Number;
      return { raw: withPrefix, formatted: normalizePhoneNumber(withPrefix) };
    }
    const raw = String(linguist?.PhoneNumber || "").trim();
    return raw ? { raw, formatted: normalizePhoneNumber(raw) } : null;
  };

  // ---- Shared row renderers (used by both the wide table and narrow cards) ----

  const buildRowView = (row) => {
    const linguist = row?.Linguist || {};
    const phone = phoneOf(linguist);
    const callPhone = phone?.raw ? phone.raw.replace(/[^0-9+]/g, "") : "";
    const isAssignedRow =
      assignedLinguist &&
      Number(assignedLinguist.id) === Number(row.LinguistID);

    const distanceMiles =
      linguistDistancesById[Number(row?.LinguistID || 0)]?.distanceMiles ?? null;
    const distanceText =
      distanceMiles != null
        ? `${distanceMiles} mi`
        : isCalculatingDistances
          ? "Calculating..."
          : "—";
    const mileageRate = getMileageRateForYear(dayjs().toDate());
    const mileageCostValue =
      distanceMiles != null ? (distanceMiles * 2 * mileageRate).toFixed(2) : null;
    const mileageCostText =
      mileageCostValue != null
        ? `~$${mileageCostValue}`
        : isCalculatingDistances
          ? "Calculating..."
          : "—";
    const stats =
      linguistClientStatsById[Number(row?.LinguistID || 0)] ||
      EMPTY_LINGUIST_CLIENT_STATS;

    return {
      linguist,
      phone,
      callPhone,
      isAssignedRow,
      distanceText,
      mileageCostText,
      completedAssignments: stats.completedAssignments,
      lastAssignmentDate: stats.lastAssignmentDate || null,
      stats,
      distanceMiles,
      linguistAddress: [
        linguist?.AddressRecord?.AddressLine1,
        linguist?.AddressRecord?.AddressLine2,
        linguist?.AddressRecord?.AddressLine3,
      ]
        .filter(Boolean)
        .join(", ") || String(linguist?.Address || "").trim(),
      mapsDirectionsUrl:
        destinationAddress && (linguist?.AddressRecord || linguist?.Address)
          ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
              [
                linguist?.AddressRecord?.AddressLine1,
                linguist?.AddressRecord?.AddressLine2,
                linguist?.AddressRecord?.AddressLine3,
              ]
                .filter(Boolean)
                .join(", ") || String(linguist?.Address || "").trim(),
            )}&destination=${encodeURIComponent(destinationAddress)}&travelmode=driving`
          : "",
    };
  };

  const currentMileageRate = getMileageRateForYear(dayjs().toDate());

  // Map rows mirror the sorted outreach rows (and reuse buildRowView) so the
  // map and list always agree on name, phone, address and miles.
  const mapLinguists = sortedRows.map((row) => {
    const view = buildRowView(row);
    const distanceInfo =
      linguistDistancesById[Number(row?.LinguistID || 0)] || {};
    const distanceMiles = view?.distanceMiles ?? null;

    return {
      id: Number(row?.LinguistID || 0),
      name: String(view?.linguist?.Name || "").trim(),
      phone: String(view?.phone?.raw || "").trim(),
      address: String(view?.linguistAddress || "").trim(),
      distanceMiles,
      lat: normalizeCoordinate(distanceInfo?.lat),
      lon: normalizeCoordinate(distanceInfo?.lon),
      mileageCost:
        distanceMiles != null
          ? Number(distanceMiles) * 2 * currentMileageRate
          : null,
    };
  });

  const mappedLinguistCount = mapLinguists.filter(hasValidCoordinates).length;

  const assignmentForMap = useMemo(
    () =>
      assignmentCoords
        ? { ...assignmentCoords, address: destinationAddress }
        : null,
    [assignmentCoords, destinationAddress],
  );

  useEffect(() => {
    console.info("[InterpretationOutreach][Map] state", {
      isMapOpen: detailTab === 1,
      detailTab,
      rows: rows.length,
      mapLinguists: mapLinguists.length,
      mappedLinguistCount,
      hasAssignment: Boolean(assignmentForMap),
      isCalculatingDistances,
    });
  }, [
    detailTab,
    rows.length,
    mapLinguists.length,
    mappedLinguistCount,
    assignmentForMap,
    isCalculatingDistances,
  ]);

  const showMapTab = rows.length > 0;

  const overlayTabs = useMemo(
    () =>
      showMapTab
        ? [{ label: "Linguists" }, { label: "Map" }]
        : undefined,
    [showMapTab],
  );

  const mapTabActive = showMapTab && detailTab === 1;

  const handleSelectLinguistFromMap = useCallback((linguistId) => {
    const numericId = Number(linguistId);
    if (!Number.isFinite(numericId) || numericId <= 0) return;

    setSelectedLinguistId(numericId);
    window.requestAnimationFrame(() => {
      const rowElement = document.getElementById(`linguist-row-${numericId}`);
      if (rowElement?.scrollIntoView) {
        rowElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    });
  }, []);

  const requestDirections = useCallback(async (from, to) => {
    const response = await api.post("/geocoding/directions", { from, to });
    return response?.data?.geojson || null;
  }, []);

  const renderLinguistName = (row, isAssignedRow) => {
    const linguist = row?.Linguist || {};
    const stats =
      linguistClientStatsById[Number(row?.LinguistID || 0)] ||
      EMPTY_LINGUIST_CLIENT_STATS;
    const distanceMiles =
      linguistDistancesById[Number(row?.LinguistID || 0)]?.distanceMiles ?? null;

    return (
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            {buildLinguistTooltipContent(linguist, stats, distanceMiles).map(
              ([label, value, meta]) => (
                <Box
                  key={label}
                  sx={{
                    display: "flex",
                    gap: 1,
                    py: 0.25,
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      minWidth: "90px",
                      color: "inherit",
                    }}
                  >
                    {label}:
                  </Typography>
                  <Typography variant="caption" sx={{ color: "inherit" }}>
                    {String(value)}
                  </Typography>
                  {meta?.clickable && (
                    <Box sx={{ display: "inline-flex", gap: 0.5, ml: 0.5 }}>
                      <IconButton
                        size="small"
                        component="a"
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(value))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ p: 0.25, color: theme.palette.primary.main }}
                        title="Open in Maps"
                      >
                        <PersonPinCircleRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard
                            ?.writeText(String(value))
                            .then(() =>
                              showSnackbar(
                                "Address copied to clipboard",
                                theme,
                                "success",
                              ),
                            )
                            .catch(() => {});
                        }}
                        sx={{ p: 0.25, color: theme.palette.primary.main }}
                        title="Copy Address"
                      >
                        <ContentCopyRoundedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ),
            )}
          </Box>
        }
        placement="right"
        arrow
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              boxShadow: theme.shadows[4],
              border: `1px solid ${theme.palette.divider}`,
              fontSize: "0.75rem",
              maxWidth: 360,
            },
          },
          arrow: {
            sx: { color: theme.palette.background.paper },
          },
        }}
      >
        <Box sx={{ display: "inline-flex", flexDirection: "column" }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {linguist.Name || "—"}
          </Typography>
          {isAssignedRow && (
            <Chip
              label="Assigned"
              color="success"
              size="small"
              sx={{ mt: 0.5, height: 20, fontSize: "0.65rem", alignSelf: "flex-start" }}
            />
          )}
        </Box>
      </Tooltip>
    );
  };

  const renderStatusSelect = (row) => (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <Select
        value={row.Status || "Not Reached"}
        disabled={savingStatusId === row.id}
        onChange={(event) => handleStatusChange(row, event.target.value)}
        sx={{
          fontSize: "0.8rem",
          fontWeight: 600,
          color: getStatusColor(theme, row.Status),
          bgcolor: `${getStatusColor(theme, row.Status)}14`,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: `${getStatusColor(theme, row.Status)}66`,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: getStatusColor(theme, row.Status),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: getStatusColor(theme, row.Status),
          },
          "& .MuiSelect-icon": {
            color: getStatusColor(theme, row.Status),
          },
        }}
      >
        {OUTREACH_STATUSES.map((status) => {
          const color = getStatusColor(theme, status);
          return (
            <MenuItem
              key={status}
              value={status}
              sx={{
                color,
                fontWeight: 600,
                "&.Mui-selected": {
                  backgroundColor: `${color}20`,
                  color,
                },
                "&:hover": {
                  backgroundColor: `${color}14`,
                },
              }}
            >
              {status}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );

  const renderContactButtons = (row, callPhone) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Tooltip
        title={
          callPhone
            ? "Text availability (SMS with details prefilled)"
            : "This linguist has no phone number"
        }
        placement="top"
      >
        <Box component="span" sx={{ display: "inline-flex" }}>
          <IconButton
            size="small"
            onClick={() => handleTextAvailability(row, callPhone)}
            disabled={!callPhone}
            aria-label={
              callPhone
                ? "Text availability (SMS with details prefilled)"
                : "This linguist has no phone number"
            }
          >
            <SmsRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Tooltip>
      <Tooltip
        title={
          row?.Linguist?.Email
            ? "Email"
            : "This linguist has no email address"
        }
        placement="top"
      >
        <Box component="span" sx={{ display: "inline-flex" }}>
          <IconButton
            size="small"
            onClick={() => openSendDetails(row, "email")}
            disabled={!row?.Linguist?.Email}
            aria-label={
              row?.Linguist?.Email
                ? "Email"
                : "This linguist has no email address"
            }
          >
            <EmailRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Tooltip>
      <Tooltip title="History" placement="top">
        <IconButton
          size="small"
          onClick={() => setHistoryRow(historyRow === row.id ? null : row.id)}
          aria-label="History"
        >
          <HistoryRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderAssignAction = (row, isAssignedRow) => {
    if (row.Status === "Accepted" && !assignedLinguist) {
      return (
        <Button
          size="small"
          variant="contained"
          color="success"
          startIcon={<CheckCircleRoundedIcon />}
          disabled={assigningId === row.id}
          onClick={() => handleAssign(row)}
          sx={{ borderRadius: "12px" }}
        >
          Assign
        </Button>
      );
    }
    if (isAssignedRow) {
      return <Chip label="Assigned" color="success" size="small" />;
    }
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  };

  return (
    <Overlay
      id="interpretation-outreach"
      open={true}
      onClose={() => setOverlayName("")}
      title="Pre-Assign Outreach"
      maxWidthPx={1400}
      scrollable={false}
      tabs={overlayTabs}
      activeTab={detailTab}
      onTabChange={setDetailTab}
    >
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
            p: "1rem 2.3rem 1rem 2.5rem",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Assignment summary */}
          <Grid container spacing={2} sx={{ mb: 2, flexShrink: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2">
                <b>Project:</b>{" "}
                {assignment?.ProjectNumber
                  ? `N° ${assignment.ProjectNumber}`
                  : "—"}{" "}
                <b>Date:</b>{" "}
                {assignment?.Date
                  ? dayjs(assignment.Date).format("ddd, MMM DD, YYYY")
                  : "—"}{" "}
                <b>Language:</b>{" "}
                {targetLanguageName || assignment?.Details || "—"}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {assignedLinguist ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    icon={<AssignmentIndRoundedIcon />}
                    label={`Assigned: ${assignedLinguist.Name || "—"}`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    This linguist is assigned — outreach for others is
                    informational.
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No linguist assigned yet. Contact linguists below and assign
                  once someone accepts.
                </Typography>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2, flexShrink: 0 }} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 1.5,
                  flexShrink: 0,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Linguists ({rows.length})
                  {distanceSource ? (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1, fontWeight: 400 }}
                    >
                      | Source: {distanceSource.toUpperCase()}
                    </Typography>
                  ) : null}
                </Typography>
              </Box>

              {mapTabActive ? (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: { xs: 320, md: 420 },
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    <MapRoundedIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Linguist map
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {mappedLinguistCount} of {mapLinguists.length} linguist
                      {mapLinguists.length === 1 ? "" : "s"} mapped
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <Suspense
                      fallback={
                        <Box
                          sx={{
                            height: "100%",
                            minHeight: 320,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress size={26} />
                        </Box>
                      }
                    >
                      <LinguistDistanceMap
                        assignment={assignmentForMap}
                        linguists={mapLinguists}
                        selectedLinguistId={selectedLinguistId}
                        onSelectLinguist={handleSelectLinguistFromMap}
                        onRequestDirections={requestDirections}
                        height="100%"
                      />
                    </Suspense>
                  </Box>
                </Box>
              ) : (
                <>
              {rows.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: "12px",
                    py: 4,
                    textAlign: "center",
                    borderStyle: "dashed",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No active linguists match this assignment's language yet.
                  </Typography>
                </Paper>
              ) : isWide ? (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    borderRadius: "12px",
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                  }}
                >
                  <Table size="small" sx={{ borderCollapse: "separate" }}>
                    <TableHead
                      sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        bgcolor: theme.palette.background.default,
                      }}
                    >
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Distance</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Mileage Cost
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Assignments
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Last Assignment
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Last Contacted
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Contacted By
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRows.map((row) => {
                        const {
                          phone,
                          callPhone,
                          isAssignedRow,
                          distanceText,
                          mapsDirectionsUrl,
                          mileageCostText,
                          completedAssignments,
                          lastAssignmentDate,
                        } = buildRowView(row);
                        return (
                          <TableRow
                            key={row.id}
                            hover
                            id={`linguist-row-${row.LinguistID}`}
                            onMouseEnter={() =>
                              setSelectedLinguistId(Number(row.LinguistID))
                            }
                            sx={{
                              ...(isAssignedRow
                                ? { bgcolor: theme.palette.success.light + "22" }
                                : {}),
                              ...(Number(selectedLinguistId) ===
                              Number(row.LinguistID)
                                ? { bgcolor: theme.palette.action.selected }
                                : {}),
                            }}
                          >
                            <TableCell>
                              {renderLinguistName(row, isAssignedRow)}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {phone?.formatted || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">{distanceText}</Typography>
                                {distanceCalculationFailed && mapsDirectionsUrl && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    component="a"
                                    href={mapsDirectionsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<PersonPinCircleRoundedIcon />}
                                    sx={{
                                      mt: 0.5,
                                      px: 1,
                                      minHeight: 30,
                                      borderRadius: 1,
                                      fontWeight: 600,
                                      whiteSpace: "nowrap",
                                      "&:hover": { bgcolor: "action.hover" },
                                    }}
                                  >
                                    Manually Check Distance
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {mileageCostText}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {completedAssignments}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {lastAssignmentDate
                                  ? formatLastAssignmentAgo(lastAssignmentDate)
                                  : "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>{renderStatusSelect(row)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {row.LastContactedAt
                                  ? dayjs(row.LastContactedAt).format(
                                      "MMM DD, h:mm A",
                                    )
                                  : "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {row.ContactedByMember?.Name || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {renderContactButtons(row, callPhone)}
                            </TableCell>
                            <TableCell align="right">
                              {renderAssignAction(row, isAssignedRow)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                /* Narrow screens (<1200px): each linguist renders as a card
                   with all details and buttons. */
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  {sortedRows.map((row) => {
                    const {
                      phone,
                      callPhone,
                      isAssignedRow,
                      distanceText,
                      mapsDirectionsUrl,
                      mileageCostText,
                      completedAssignments,
                      lastAssignmentDate,
                    } = buildRowView(row);
                    return (
                      <Paper
                        key={row.id}
                        id={`linguist-row-${row.LinguistID}`}
                        onMouseEnter={() =>
                          setSelectedLinguistId(Number(row.LinguistID))
                        }
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                          ...(isAssignedRow
                            ? {
                                borderColor: theme.palette.success.main,
                                bgcolor: `${theme.palette.success.main}0d`,
                              }
                            : {}),
                          ...(Number(selectedLinguistId) ===
                          Number(row.LinguistID)
                            ? {
                                borderColor: theme.palette.primary.main,
                                boxShadow: `0 0 0 2px ${theme.palette.primary.main}22`,
                              }
                            : {}),
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          {renderLinguistName(row, isAssignedRow)}
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {renderContactButtons(row, callPhone)}
                          </Box>
                        </Box>

                        <Grid container spacing={1.5}>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Phone
                            </Typography>
                            <Typography variant="body2">
                              {phone?.formatted || "—"}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Distance
                            </Typography>
                            <Typography variant="body2">{distanceText}</Typography>
                            {distanceCalculationFailed && mapsDirectionsUrl && (
                              <Button
                                size="small"
                                variant="outlined"
                                component="a"
                                href={mapsDirectionsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<PersonPinCircleRoundedIcon />}
                                sx={{
                                  mt: 0.5,
                                  px: 1,
                                  minHeight: 30,
                                  borderRadius: 1,
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                  "&:hover": { bgcolor: "action.hover" },
                                }}
                              >
                                Manually Check Distance
                              </Button>
                            )}
                          </Grid>
                          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Mileage Cost
                            </Typography>
                            <Typography variant="body2">
                              {mileageCostText}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Assignments
                            </Typography>
                            <Typography variant="body2">
                              {completedAssignments}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Last Assignment
                            </Typography>
                            <Typography variant="body2">
                              {lastAssignmentDate
                                ? formatLastAssignmentAgo(lastAssignmentDate)
                                : "—"}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Last Contacted
                            </Typography>
                            <Typography variant="body2">
                              {row.LastContactedAt
                                ? dayjs(row.LastContactedAt).format(
                                    "MMM DD, h:mm A",
                                  )
                                : "—"}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Contacted By
                            </Typography>
                            <Typography variant="body2">
                              {row.ContactedByMember?.Name || "—"}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Status
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              {renderStatusSelect(row)}
                            </Box>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Action
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              {renderAssignAction(row, isAssignedRow)}
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}
                </Box>
              )}

              {historyRow !== null && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.primary.border2}`,
                    bgcolor: theme.palette.background.default,
                    flexShrink: 0,
                    maxHeight: "40%",
                    overflowY: "auto",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    History —{" "}
                    {rows.find((r) => Number(r.id) === Number(historyRow))
                      ?.Linguist?.Name || ""}
                  </Typography>
                  {renderHistory(
                    rows.find((r) => Number(r.id) === Number(historyRow)),
                  )}
                </Box>
              )}
                </>)}
            </>
          )}
        </Box>
    </Overlay>
  );
};

export default InterpretationOutreach;
